<template>
  <ElDropdown
    :show-timeout="0"
    trigger="click"
    placement="bottom"
    class="h-full text-white"
    @command="onDropdownCommand"
  >
    <div class="h-full flex flex-row items-center max-w-275 outline:none laptop-l:max-w-300">
      <div class="relative flex-shrink-0">
        <AvatarChip
          :initials="userInitials"
          :name-length="userAuthData.displayName.length"
          background-color="#f3f3f3"
          color="#333333"
          :src="userAuthData.guestPhotoUrl"
          :size="45"
        />
        <!-- On phones the plan name next to the avatar is hidden, so subscribers get a crown -->
        <span
          v-if="activeSubscriptionPlanPackage"
          :title="subscriberLabel"
          :aria-label="subscriberLabel"
          class="subscriber-crown"
        >
          <FontAwesomeIcon :icon="['fas', 'crown']" />
        </span>
      </div>
      <div
        class="hidden ml-15 truncate laptop:block"
        :class="{ 'mb-2': activeSubscriptionPlanPackage }"
      >
        <p class="flex flex-row items-end font-medium text-base mb-2 truncate">
          <span class="leading-none truncate">
            {{ userAuthData.displayName }}
          </span>
          <span
            v-if="activeSubscriptionPlanPackage"
            class="flex-shrink-0 leading-tight tracking-wide text-sm text-white uppercase bg-secondary px-10
            py-2 ml-5 rounded"
          >
            {{ activeSubscriptionPlanPackage.fields.shortName || 'Sub' }}
          </span>
        </p>
        <p class="text-sm leading-tight truncate">{{ userAuthData.email }}</p>
      </div>
      <FontAwesomeIcon
        :icon="['far', 'angle-down']"
        class="hidden self-center ml-15 laptop:block"
      />
    </div>

    <ElDropdownMenu
      slot="dropdown"
      class="dropdown-menu"
    >
      <ElDropdownItem
        key="user-account-details"
        class="border-b border-gray-200"
      >
        <RouterLink
          :to="{ name: 'UserProfile' }"
          tag="div"
          class="flex items-center"
        >
          <AvatarChip
            :initials="userInitials"
            :name-length="userAuthData.displayName.length"
            background-color="#333333"
            color="#f8f8f8"
            :src="userAuthData.guestPhotoUrl"
            :size="60"
            class="flex-shrink-0"
          />
          <div class="ml-20 my-5">
            <p class="flex flex-row items-end font-medium text-base truncate mb-2">
              <span class="font-bold text-primary leading-none truncate">
                {{ userAuthData.displayName }}
              </span>
              <span
                v-if="activeSubscriptionPlanPackage"
                class="flex-shrink-0 text-sm text-white leading-tight uppercase bg-secondary px-15
                py-5 ml-10 rounded"
              >
                {{ activeSubscriptionPlanPackage.fields.shortName || 'Sub' }}
              </span>
            </p>
            <p class="text-sm leading-normal truncate">{{ userAuthData.email }}</p>
            <p class="font-medium text-gray-600 hover:text-gray-400">View profile</p>
          </div>
        </RouterLink>
      </ElDropdownItem>
      <div
        v-if="$route.name === 'Dashboard' && isMobileScreen"
        class="border-b border-gray-200">
        <ElDropdownItem
          v-for="(item, index) in dropdownItemsNavigationTeam"
          :key="index"
          :command="item.command"
        >
          <FontAwesomeIcon
            v-if="item.iconClassNames"
            fixed-width
            :icon="item.iconClassNames"
            class="text-lg ml-5 mr-15"
          />
          {{ item.label }}
        </ElDropdownItem>
      </div>
      <div
        v-if="$route.name === 'TeamManagement' && isMobileScreen"
        class="border-b border-gray-200">
        <ElDropdownItem
          v-for="(item, index) in dropdownItemsNavigationEvents"
          :key="index"
          :command="item.command"
        >
          <FontAwesomeIcon
            v-if="item.iconClassNames"
            fixed-width
            :icon="item.iconClassNames"
            class="text-lg ml-5 mr-15"
          />
          {{ item.label }}
        </ElDropdownItem>
      </div>
      <div
        v-if="$route.name === 'EventDetails' && isMobileScreen"
        class="border-b border-gray-200">
        <ElDropdownItem
          v-for="(item, index) in dropdownItemsSettings"
          :key="index"
          :command="item.command"
        >
          <FontAwesomeIcon
            v-if="item.iconClassNames"
            fixed-width
            :icon="item.iconClassNames"
            class="text-lg ml-5 mr-15"
          />
          {{ item.label }}
        </ElDropdownItem>
      </div>
      <ElDropdownItem
        v-for="(item, index) in dropdownItems"
        :key="index"
        :command="item.command"
      >
        <FontAwesomeIcon
          v-if="item.iconClassNames"
          fixed-width
          :icon="item.iconClassNames"
          class="text-lg ml-5 mr-15"
        />
        {{ item.label }}
      </ElDropdownItem>

    </ElDropdownMenu>
  </ElDropdown>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';
import AvatarChip from './AvatarChip';

export default {
  components: {
    AvatarChip,
  },
  computed: {
    isMobileScreen() {
      return window.innerWidth < 768;
    },
    ...mapState('user/events', {
      allEvents: 'all',
    }),
    currentEvent() {
      return this.allEvents[this.$route.params.eventID];
    },
    ...mapState('intercom', {
      intercomMessengerIsVisible: 'messengerIsVisible',
      intercomUnreadMessagesCount: 'unreadMessagesCount',
    }),
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    ...mapGetters('user/package', {
      activeSubscriptionPlanPackage: 'activeSubscriptionPlanPackage',
    }),
    subscriberLabel() {
      const plan = this.activeSubscriptionPlanPackage;

      return plan ? `${plan.fields.shortName || 'Eventor'} subscriber` : '';
    },
    userInitials() {
      if (!this.userAuthData.displayName) return '';

      const firstPart = this.userAuthData.displayName.substr(0, this.userAuthData.displayName.indexOf(' '));
      const lastPart = this.userAuthData.displayName.substr(this.userAuthData.displayName.indexOf(' ') + 1);

      return firstPart.charAt(0) + lastPart.charAt(0) || '';
    },
    isCurrentEventOwner() {
      return !!this.currentEvent && this.currentEvent.ownerId === this.userAuthData.id;
    },
    dropdownItemsSettings() {
      // Settings and Upgrade belong to the event owner, same as the desktop sidebar
      return [{
        label: 'Settings',
        iconClassNames: ['fad', 'cogs'],
        command: 'settings',
        isVisible: !this.activeSubscriptionPlanPackage && this.isCurrentEventOwner,
      }, {
        label: 'Upgrade',
        iconClassNames: ['fad', 'angle-double-up'],
        command: 'upgrade',
        isVisible: !this.activeSubscriptionPlanPackage && this.isCurrentEventOwner,
      }, {
        // Phones have no sidebar, so the event page menu also links to the team page
        label: 'Team',
        iconClassNames: ['fas', 'user-friends'],
        command: 'team',
        isVisible: true,
      }].filter((item1) => item1.isVisible);
    },
    dropdownItemsNavigationTeam() {
      return [{
        label: 'Team',
        iconClassNames: ['fas', 'user-friends'],
        command: 'team',
        // Teams are used by subscribers too, so the link is not tied to the subscription state
        isVisible: true,
      }].filter((item1) => item1.isVisible);
    },
    dropdownItemsNavigationEvents() {
      return [{
        label: 'Events',
        iconClassNames: ['fad', 'calendar-day'],
        command: 'events',
        isVisible: !this.activeSubscriptionPlanPackage,
      }].filter((item1) => item1.isVisible);
    },
    dropdownItems() {
      return [{
        label: 'Subscribe & Save',
        iconClassNames: ['fas', 'crown'],
        command: 'subscribe',
        isVisible: !this.activeSubscriptionPlanPackage,
      }, {
        label: 'Support chat',
        iconClassNames: ['fas', 'life-ring'],
        command: 'toggle-intercom-messenger',
        isVisible: true,
      }, {
        label: 'Sign out',
        iconClassNames: ['fas', 'power-off'],
        command: 'sign-out',
        isVisible: true,
      }].filter((item) => item.isVisible);
    },
  },
  methods: {
    ...mapActions('intercom', {
      updateMessengerIsVisible: 'updateMessengerIsVisible',
    }),
    ...mapActions('user/auth', {
      signOut: 'signOut',
    }),
    onDropdownCommand(command) {
      switch (command) {
        case 'toggle-intercom-messenger':
          this.updateMessengerIsVisible(!this.intercomMessengerIsVisible);
          break;
        case 'sign-out':
          this.signOut()
            .then(() => {
              showSuccessNotification('You signed out successfully.');
            })
            .catch((error) => {
              showErrorNotification(error.message);
            });
          break;
        case 'subscribe':
          this.$router.push({ name: 'SubscriptionPlans' });
          break;
        case 'team':
          this.$router.push({ name: 'TeamManagement' });
          break;
        case 'events':
          this.$router.push({ name: 'Dashboard' });
          break;
        case 'settings':
          this.$router.push({ name: 'EventUpdate' });
          break;
        case 'upgrade':
          this.$router.push({ name: 'EventUpgrade' });
          break;
        default:
          console.log(`No command ${command}`);
      }
    },

  },
};
</script>

<style scoped>
.subscriber-crown {
  @apply absolute flex items-center justify-center text-white bg-secondary rounded-full;

  right: -2px;
  bottom: -2px;
  width: 20px;
  height: 20px;
  font-size: 10px;
  box-shadow: 0 0 0 2px hsl(0, 0%, 20%);
}

.dropdown-menu {
  @apply p-0 !important;
}

.el-dropdown-menu__item {
  @apply text-base mt-0 py-8 !important;

  transition: all 0.25s ease;
}
</style>
